import React, { useEffect, useRef, useMemo, useState, Suspense } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { useGLTF, Environment, useTexture, Sphere, ContactShadows, Html, useProgress } from "@react-three/drei";
import * as THREE from "three";

import autoBetGlobe from "../../../../../static/three_assets/models/autobet-globe/autobet.glb";
import baseTexture from "../../../../../static/three_assets/shaders/base.jpg";
import goldMatcap from "../../../../../static/three_assets/shaders/gold_shader_material.jpg";
import earthTexture from "../../../../../static/three_assets/textures/earth/earthImage.jpg";
import earthNormalMap from "../../../../../static/three_assets/textures/earth/earthImage_normals.jpg";
import earthRoughnessMap from "../../../../../static/three_assets/textures/earth/earthImage_Roughness.jpg";
import eightBallTexture from "../../../../../static/three_assets/textures/eight_ball/eightBall1.jpg";
import eightBall2Texture from "../../../../../static/three_assets/textures/eight_ball/eightBall2.jpg";

const SLOT_MACHINE_DISABLED = true;
const SPHERE_DISABLED = true;

class WebGLErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasWebGLCrash: false };
  }

  static getDerivedStateFromError(error) {
    return { hasWebGLCrash: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("AutoBet 3D Engine Offline: VRAM Exhausted.", error);
  }

  render() {
    if (this.state.hasWebGLCrash) {
      return (
        <div className="flex flex-col items-center justify-center w-full h-full min-h-[320px] bg-[#050505] rounded-2xl border border-gray-800/50 shadow-2xl p-6">
          <div className="text-blue-500 mb-4 animate-pulse">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-white text-lg font-bold tracking-wider mb-2">GPU SYNC LOST</h3>
          <p className="text-gray-400 text-sm mb-6 text-center max-w-xs">
            Background rendering was suspended to save system memory.
          </p>
          <button 
            onClick={() => this.setState({ hasWebGLCrash: false })}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition-all duration-300 shadow-[0_0_15px_rgba(37,99,235,0.5)]"
          >
            REBOOT 3D ENGINE
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function CanvasLoader() {
  const { progress } = useProgress();
  return (
    <Html center>
      <div className="flex flex-col items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-2"></div>
        <span className="text-blue-600 font-bold tracking-widest text-xs">
          {progress.toFixed(0)}%
        </span>
      </div>
    </Html>
  );
}

function Model() {
  const { scene: gltfScene } = useGLTF(autoBetGlobe);
  const scene = useMemo(() => gltfScene.clone(), [gltfScene]);
  const { gl } = useThree();

  useEffect(() => {
    return () => {
      const ext = gl.getContext().getExtension('WEBGL_lose_context');
      if (ext) ext.loseContext();
    };
  }, [gl]);

  const globeRef = useRef(null);
  const poolBallsRef = useRef([]);
  const coinsRef = useRef([]);
  const dragState = useRef(null);

  const textures = useTexture({
    baseColor: earthTexture,
    normal: earthNormalMap,
    roughness: earthRoughnessMap,
    eightBall1: eightBallTexture,
    eightBall2: eightBall2Texture,
    gold: goldMatcap,
    alpha: baseTexture,
  });

  useMemo(() => {
    Object.values(textures).forEach((tex) => {
      tex.flipY = true; 
      tex.anisotropy = 16;
      tex.needsUpdate = true;
    });

    textures.baseColor.colorSpace = THREE.SRGBColorSpace;
    textures.eightBall1.colorSpace = THREE.SRGBColorSpace;
    textures.eightBall2.colorSpace = THREE.SRGBColorSpace;
    textures.gold.colorSpace = THREE.SRGBColorSpace;
    textures.normal.colorSpace = THREE.NoColorSpace;
    textures.roughness.colorSpace = THREE.NoColorSpace;
  }, [textures]);

  useEffect(() => {
    poolBallsRef.current = [];
    coinsRef.current = [];
    const createdMaterials = [];

    scene.traverse((child) => {
      if (!child.isMesh) return;
      const meshName = child.name.toLowerCase();

      child.castShadow = true;
      child.receiveShadow = true;

      if (SLOT_MACHINE_DISABLED && (meshName.includes("slot") || meshName.includes("reel") || meshName.includes("cylinder") || meshName.includes("lever") || meshName === "side_cap1")) {
        child.visible = false;
        return;
      }
      if (SPHERE_DISABLED && meshName === "sphere") {
        child.visible = false;
        return;
      }
      if (meshName.includes("plane") || meshName.includes("base002") || meshName.includes("pookies_modifier") || meshName === "globe") {
        child.visible = false;
        return;
      }

      if (meshName.includes("coin")) {
        coinsRef.current.push(child);
        child.userData.rotSpeedX = (Math.random() - 0.5) * 0.4;
        child.userData.rotSpeedY = (Math.random() - 0.5) * 0.4;
        child.userData.rotSpeedZ = (Math.random() - 0.5) * 0.4;

        const oldMat = child.material;
        child.material = new THREE.MeshPhysicalMaterial({
          map: oldMat.map || null,
          color: oldMat.color || new THREE.Color(0xffffff),
          roughness: 0.1,
          metalness: 0.3,
          clearcoat: 0.8,
          clearcoatRoughness: 0.1,
          envMapIntensity: 2.0,
        });
        if (oldMat.vertexColors) child.material.vertexColors = true;
        child.material.needsUpdate = true;
        createdMaterials.push(child.material);
      }

      if (meshName === "8poolball_1" || meshName === "8poolball_2") {
        poolBallsRef.current.push(child);
        
        if (meshName === "8poolball_2") {
          child.position.y -= 2.5; 
          child.position.x += 1.5;
          child.position.z += 1.0;
        }

        child.userData.rotSpeedX = (Math.random() - 0.5) * 3.5;
        child.userData.rotSpeedY = (Math.random() - 0.5) * 3.5;
        child.userData.rotSpeedZ = (Math.random() - 0.5) * 3.5;
        
        child.geometry.computeBoundingSphere();
        const radius = child.geometry.boundingSphere.radius;
        child.geometry = new THREE.SphereGeometry(radius, 64, 64);

        child.material = new THREE.MeshPhysicalMaterial({
          map: meshName === "8poolball_2" ? textures.eightBall2 : textures.eightBall1,
          roughness: 0.1,
          metalness: 0.1,
          clearcoat: 0.6,
          envMapIntensity: 2.0,
        });

        child.rotation.set(0, Math.PI / 2, 0);
        child.material.needsUpdate = true;
        createdMaterials.push(child.material);
      }

      if (meshName === "base_main" || meshName === "circle") {
        child.material = new THREE.MeshPhysicalMaterial({
          color: new THREE.Color("#050505"), 
          metalness: 0.8,
          roughness: 0.2,
          clearcoat: 0.3,
          envMapIntensity: 1.5,
        });
        createdMaterials.push(child.material);
      }

      if (meshName.includes("gold")) {
        child.material = new THREE.MeshMatcapMaterial({ matcap: textures.gold });
        createdMaterials.push(child.material);
      }

      if (meshName.includes("glow_base")) {
        child.material = new THREE.MeshStandardMaterial({
          color: 0xffffff,
          emissive: "#2563EB", 
          emissiveIntensity: 6.0,
          toneMapped: false,
        });
        createdMaterials.push(child.material);

        if (!child.userData.hasLight) {
          const glowLight = new THREE.PointLight("#2563EB", 120, 20); 
          glowLight.position.set(0, 1.5, 0); 
          
          glowLight.castShadow = true;
          glowLight.shadow.mapSize.width = 1024;
          glowLight.shadow.mapSize.height = 1024;
          glowLight.shadow.bias = -0.002; 
          
          child.add(glowLight);
          child.userData.hasLight = true;
        }
      }
    });

    return () => {
      createdMaterials.forEach((mat) => mat.dispose());
    };
  }, [scene, textures]);

  useFrame((state, delta) => {
    if (globeRef.current) globeRef.current.rotation.y += delta * 0.15;

    coinsRef.current.forEach((coin) => {
      if (dragState.current?.object !== coin) {
        coin.rotation.x += delta * coin.userData.rotSpeedX;
        coin.rotation.y += delta * coin.userData.rotSpeedY;
        coin.rotation.z += delta * coin.userData.rotSpeedZ;
      }
    });

    poolBallsRef.current.forEach((ball) => {
      if (dragState.current?.object !== ball) {
        ball.rotation.x += delta * ball.userData.rotSpeedX;
        ball.rotation.y += delta * ball.userData.rotSpeedY;
        ball.rotation.z += delta * ball.userData.rotSpeedZ;
      }
    });

    if (dragState.current) {
      const drag = dragState.current;
      drag.velX = THREE.MathUtils.lerp(drag.velX, 0, 0.08);
      drag.velY = THREE.MathUtils.lerp(drag.velY, 0, 0.08);

      drag.object.rotateX(drag.velX);
      drag.object.rotateY(drag.velY);

      if (!drag.isDragging && Math.abs(drag.velX) < 0.001 && Math.abs(drag.velY) < 0.001) {
        dragState.current = null;
      }
    }
  });

  return (
    <group position={[0, -2.5, 0]}>
      <primitive
        object={scene}
        dispose={null}
        onPointerDown={(e) => {
          if (!e.object.visible) return;
          if (coinsRef.current.includes(e.object) || poolBallsRef.current.includes(e.object)) {
            e.stopPropagation();
            e.target.setPointerCapture(e.pointerId);
            dragState.current = {
              object: e.object,
              lastX: e.clientX,
              lastY: e.clientY,
              velX: 0,
              velY: 0,
              isDragging: true,
            };
            document.body.style.cursor = "grabbing";
          }
        }}
        onPointerMove={(e) => {
          if (dragState.current?.isDragging && dragState.current.object === e.object) {
            const deltaX = e.clientX - dragState.current.lastX;
            const deltaY = e.clientY - dragState.current.lastY;
            dragState.current.velX += deltaY * 0.005;
            dragState.current.velY += deltaX * 0.005;
            dragState.current.lastX = e.clientX;
            dragState.current.lastY = e.clientY;
          }
        }}
        onPointerUp={(e) => {
          if (dragState.current) {
            e.target.releasePointerCapture(e.pointerId);
            dragState.current.isDragging = false;
            document.body.style.cursor = "auto";
          }
        }}
        onPointerOver={(e) => {
          if (coinsRef.current.includes(e.object) || poolBallsRef.current.includes(e.object)) {
            document.body.style.cursor = "grab";
          }
        }}
        onPointerOut={() => {
          if (!dragState.current?.isDragging) {
            document.body.style.cursor = "auto";
          }
        }}
      />

      <Sphere
        ref={globeRef}
        args={[2.5, 64, 64]} 
        position={[0, 3.5, 0]}
        rotation={[0, Math.PI / 2, 0]}
        castShadow
        receiveShadow
      >
        <meshPhysicalMaterial
          map={textures.baseColor}
          normalMap={textures.normal}
          roughnessMap={textures.roughness}
          roughness={0.7}
          metalness={0.2}
          clearcoat={0.3}
          clearcoatRoughness={0.1}
          envMapIntensity={1.2}
        />
      </Sphere>

      <ContactShadows 
        position={[0, -2.6, 0]} 
        opacity={0.85} 
        scale={25} 
        blur={2.5} 
        far={10} 
        resolution={512} 
        color="#000000" 
      />
    </group>
  );
}

export default function AutoBetGlobe({ className, style }) {
  const [isTabActive, setIsTabActive] = useState(true);

  useEffect(() => {
    const handleVisibility = () => setIsTabActive(!document.hidden);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  return (
    <div className={className} style={{ width: "100%", height: "100%", minHeight: 320, ...style }}>
      <WebGLErrorBoundary>
        <Canvas
          shadows 
          frameloop={isTabActive ? "always" : "never"}
          dpr={[1, 1.5]} 
          gl={{ 
            antialias: true, 
            alpha: true, 
            toneMapping: THREE.ACESFilmicToneMapping, 
            toneMappingExposure: 1,
            powerPreference: "high-performance",
            failIfMajorPerformanceCaveat: false
          }}
          camera={{ position: [17, 26, 7], fov: 13 }}
        >
          <Suspense fallback={<CanvasLoader />}>
            <Environment preset="studio" environmentIntensity={0.45} background={false} />
            <Model />
          </Suspense>
        </Canvas>
      </WebGLErrorBoundary>
    </div>
  );
}

useGLTF.preload(autoBetGlobe);